import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { layoutModeAtom } from '../../atoms/v3/uiAtoms';
import { getPlatformInfo } from '../../utils/platformUtils';

interface UndoToastProps {
  taskTitle: string;
  isCompleted: boolean;
  onUndo: () => void;
  onDismiss: () => void;
  duration?: number;
}

/**
 * 撤销操作的 Toast 组件
 * 使用低调的银色/灰色设计，支持日间夜间模式
 */
export const UndoToast: React.FC<UndoToastProps> = ({
  taskTitle,
  isCompleted,
  onUndo,
  onDismiss,
  duration = 5000
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const layoutMode = useAtomValue(layoutModeAtom);
  const platformInfo = getPlatformInfo();

  // 自动消失
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  const handleUndo = () => {
    onUndo();
    onDismiss();
  };

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-toast-slide-up">
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-xl shadow-lg px-4 py-3 max-w-sm mx-4">
        <div className="flex items-center justify-between">
          {/* 左侧内容 */}
          <div className="flex-1 mr-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {isCompleted
                ? t('v3/tasks:toast.taskCompleted', '任务已完成')
                : t('v3/tasks:toast.taskUncompleted', '任务已取消完成')
              }
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
              {taskTitle}
            </div>
          </div>

          {/* 撤销按钮 */}
          <button
            onClick={handleUndo}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors duration-200"
          >
            <span>{t('common:actions.undo', '撤销')}</span>
            {/* 桌面端显示快捷键提示 */}
            {layoutMode === 'desktop' && (
              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                ({platformInfo.isMacOS ? 'Cmd+Z' : 'Ctrl+Z'})
              </span>
            )}
          </button>

          {/* 关闭按钮 */}
          <button
            onClick={onDismiss}
            className="ml-2 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            aria-label={t('common:actions.close', '关闭')}
          >
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
